import { Fragment, useCallback, useMemo, useState } from 'react';

import classnames from 'classnames';

import Drawer from 'components/uiLibrary/Drawer';
import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';
import LinkButton, { TextButton } from 'components/uiLibrary/LinkButton';
import Loader from 'components/uiLibrary/Loader';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Tooltip from 'components/uiLibrary/Tooltip';
import Typography from 'components/uiLibrary/Typography';

import AddAnotherRole from 'components/Casting/Display/AddAnotherRole';
import CastingMobileSearch from 'components/Casting/Display/CastingMobileSearch';
import PricingPlanInfo from 'components/Casting/Display/PricingPlanInfo';
// eslint-disable-next-line import/no-cycle
import RoleOverlap from 'components/Casting/Display/RoleOverlap';
import RoleRepertoireDrawer from 'components/Casting/Display/RoleRepertoireDrawer';
import { CATEGORIES, COMPONENTS, SECTIONS, SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import ArtistProfessionDropdown from 'components/Globals/FormFields/Display/ArtistProfessionDropdown';
import { useShowPageAccessPortalForCasting } from 'components/Globals/PortalsProvider';
import ShareEntity from 'components/Globals/ShareEntity';
import ProductionProSlug from 'components/Productions/Display/Slugs/ProductionProSlug';
import AdvancedSearch from 'components/Search/AdvancedSearch';

import { CASTING_TOOL_TABS, TP, URL_SLUG_TYPES } from 'constants/index';

import { getBooleanSearchQuery } from 'utils/casting';
import useAppContext from 'utils/hooks/useAppContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import { useDialogs } from 'utils/hooks/useDialogs';
import usePageContext from 'utils/hooks/usePageContext';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Productions/queries';

import { useTranslation } from 'src/i18n';

import {
  CASTING_RESULT_TYPES_VALUES,
  ENTITY_TYPES,
  FILTER_TYPES,
  PROFESSION_TYPES,
  ROUTE_RESERVED_KEYWORDS,
} from 'operabase-router/constants';

import HeaderSearch from '../HeaderSearch';
import History from '../History';

import CastingResultTypeTabs from './CastingResultTypeTabs';
import classes from './CastingSearch.module.scss';
import DesktopSearch from './DesktopSearch';

const trackingData = {
  section: SECTIONS.GLOBAL_HEADER,
};

const oldSearchTrackingData = {
  section: SECTIONS.OLD_GLOBAL_SEARCH,
};

const artistProfessionTrackingData = {
  ...trackingData,
  component: COMPONENTS.FILTERS,
  category: CATEGORIES.SEARCH,
};

const castingToolTrackingData = {
  section: SECTIONS.CASTING_TOOL_HEADER,
};

// Helper function to determine profession type from API data
const getProfessionType = profession => {
  if (profession?.type === PROFESSION_TYPES.VOICE_TYPE) {
    return PROFESSION_TYPES.VOICE_TYPE;
  }

  if (profession?.type === PROFESSION_TYPES.INSTRUMENTALIST) {
    return PROFESSION_TYPES.INSTRUMENTALIST;
  }

  return PROFESSION_TYPES.PROFESSION;
};

// Helper function to show value only if it matches the dropdown type
const getSelectedValueByProfessionType = (selectedProfession, dropdownTypes) => {
  if (!selectedProfession || !dropdownTypes?.length) return null;

  const professionType = getProfessionType(selectedProfession);
  return dropdownTypes.includes(professionType) ? selectedProfession : null;
};

const professionFields = [
  {
    types: [PROFESSION_TYPES.PROFESSION],
    trackingData: { ...artistProfessionTrackingData, subComponent: SUB_COMPONENTS.PROFESSIONS },
  },
  {
    types: [PROFESSION_TYPES.INSTRUMENTALIST],
    trackingData: { ...artistProfessionTrackingData, subComponent: SUB_COMPONENTS.INSTRUMENTALISTS },
  },
  {
    types: [PROFESSION_TYPES.VOICE_TYPE],
    trackingData: { ...artistProfessionTrackingData, subComponent: SUB_COMPONENTS.VOICE_TYPES },
  },
];

// eslint-disable-next-line no-shadow
const ProductionSlug = ({ trackingData = {} }) => {
  const { obRouteContext } = useAppContext();
  const { linkProps } = obRouteContext || {};
  const { filters } = linkProps || {};
  const production_id = filters?.[FILTER_TYPES.PRODUCTION_ID];
  const { data: production, isLoading } = useQuery(
    queries.getProductionDetails({
      id: production_id,
      queryConfig: { enabled: !!production_id },
    }),
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <ProductionProSlug
      key={production?.id}
      production={production}
      showWorkDetails
      isJumpInSlug
      trackingData={trackingData}
    />
  );
};

const HelpModal = ({ onClose }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');

  return (
    <Drawer isOpen onClose={onClose} className={classes.drawer}>
      <div className={classes.drawer__header}>
        <SpriteIcon icon="close" size={16} onClick={onClose} />
      </div>
      <div className={classes.drawer__content}>
        <Typography size={13}>{t(`${TP}.ROLE_OVERLAP_TOOTLIP_CONTENT`)}</Typography>
      </div>
    </Drawer>
  );
};

const Search = ({ isJumpInTab }) => {
  if (isJumpInTab) {
    return (
      <ProductionSlug
        trackingData={{
          ...trackingData,
          component: COMPONENTS.PRODUCTION_SLUG,
        }}
      />
    );
  }

  return <DesktopSearch trackingData={castingToolTrackingData} />;
};

const CastingSearchSearchContent = ({ isJumpInTab, selectedProfession, onSelectProfession, hasCastingToolAccess }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { obRouteContext } = useAppContext();
  const { isDesktop, isMobile } = useDeviceTypeLayouts();
  const { filters } = obRouteContext?.linkProps || {};
  const [isOpenRoleDrawer, setIsOpenRoleDrawer] = useState(false);
  const [isOpenHelpModal, setIsOpenHelpModal] = useState(false);
  const booleanQuery = useMemo(() => getBooleanSearchQuery(filters?.expression), [filters?.expression]);
  const showAddAnotherRole = booleanQuery?.[0]?.value?.[URL_SLUG_TYPES.WORK_ROLE]?.id;

  if (!isDesktop) {
    return (
      <div>
        <HorizontalScroller hideArrow>
          <div className={classes.castingToolContent}>
            <div className={classes.castingToolContent__titleContainer}>
              <Typography size="13" secondaryFont className={classes.castingToolContent__title}>
                {t(`${TP}.FN_CASTINGTOOL`)} &amp; {t(`${TP}.TALENT_SEARCH`)}
              </Typography>
              <PricingPlanInfo />
            </div>
            <div className={classes.castingToolContent__divider} />
            {isMobile ? (
              <div className={classes.castingToolContent__professionDropdown}>
                {professionFields?.map((fieldProps, index) => (
                  <ArtistProfessionDropdown
                    key={index}
                    className={classnames(classes.professionDropdown, {
                      [classes.professionDropdown_profession]: [
                        PROFESSION_TYPES.PROFESSION,
                        PROFESSION_TYPES.VOICE_TYPE,
                      ].includes(fieldProps.types[0]),
                    })}
                    value={getSelectedValueByProfessionType(selectedProfession, fieldProps.types)}
                    onChange={onSelectProfession}
                    variant="standard"
                    {...fieldProps}
                  />
                ))}
              </div>
            ) : (
              <div className={classes.castingToolContent__searchContainer}>
                <div className={classes.advancedSearch}>
                  <AdvancedSearch isCasting trackingData={castingToolTrackingData} />
                </div>
                <History isCasting trackingData={castingToolTrackingData} />
              </div>
            )}
          </div>
        </HorizontalScroller>
        {!isMobile && booleanQuery?.[0]?.value?.[URL_SLUG_TYPES.WORK_ROLE]?.id && (
          <>
            <div className={classes.castingToolContent__searchDivider} />
            <div className={classes.tabs}>
              <CastingResultTypeTabs
                trackingData={{
                  ...castingToolTrackingData,
                  component: COMPONENTS.TABS,
                }}
              />
            </div>
            <div className={classnames(classes.searchWrapper, { [classes.searchWrapperJumpIn]: !!isJumpInTab })}>
              {isJumpInTab ? (
                <ProductionSlug />
              ) : (
                <CastingMobileSearch booleanQuery={booleanQuery} onClick={() => setIsOpenRoleDrawer(true)} />
              )}
              {!isJumpInTab && (
                <ShareEntity
                  showLabelText={false}
                  className={classes.shareEntity}
                  trackingData={{ section: SECTIONS.CASTING_TOOL, component: COMPONENTS.SHARE_CTA }}
                  sticky
                />
              )}
            </div>
            <HorizontalScroller hideArrow>
              <div className={classes.workRolesMobile}>
                <div className={classes.workRolesMobile__helpIcon} onClick={() => setIsOpenHelpModal(true)}>
                  <SpriteIcon icon="help" size={16} />
                </div>
                <RoleOverlap isCastingTool limit={10} trackingData={castingToolTrackingData} />
              </div>
            </HorizontalScroller>
          </>
        )}
        {isOpenRoleDrawer && (
          <RoleRepertoireDrawer
            onClose={() => setIsOpenRoleDrawer(false)}
            trackingData={{
              section: SECTIONS.CASTING_SEARCH,
            }}
          />
        )}
        {isOpenHelpModal && <HelpModal onClose={() => setIsOpenHelpModal(false)} />}
      </div>
    );
  }

  return (
    <>
      <div
        className={classnames(classes.castingToolContent__tabs, {
          [classes.castingToolContent__tabs_noAccess]: !hasCastingToolAccess,
        })}
      >
        <CastingResultTypeTabs
          trackingData={{
            ...castingToolTrackingData,
            component: COMPONENTS.TABS,
          }}
        />
        <div className={classes.castingToolContent__professionDropdown}>
          {professionFields?.map((fieldProps, index) => (
            <ArtistProfessionDropdown
              key={index}
              className={classnames(classes.professionDropdown, {
                [classes.professionDropdown_profession]: [
                  PROFESSION_TYPES.PROFESSION,
                  PROFESSION_TYPES.VOICE_TYPE,
                ].includes(fieldProps.types[0]),
              })}
              value={getSelectedValueByProfessionType(selectedProfession, fieldProps.types)}
              onChange={onSelectProfession}
              variant="standard"
              {...fieldProps}
            />
          ))}
        </div>
      </div>
      {hasCastingToolAccess && <Search isJumpInTab={isJumpInTab} trackingData={castingToolTrackingData} />}
      {hasCastingToolAccess && booleanQuery?.[0]?.value?.[URL_SLUG_TYPES.WORK_ROLE]?.id && (
        <div className={classes.roleOverlapAddRole}>
          {showAddAnotherRole && <AddAnotherRole trackingData={castingToolTrackingData} />}
          <Tooltip
            title={t(`${TP}.ROLE_OVERLAP_TOOLTIP_TITLE`)}
            placement="top"
            className={classes.roleOverlapAddRole__tooltip}
            enterDelay={1000}
          >
            <div className={classes.roleOverlapAddRole__toolTipWrapper}>
              <RoleOverlap limit={5} trackingData={castingToolTrackingData} />
            </div>
          </Tooltip>
        </div>
      )}
    </>
  );
};

const ProViewContent = ({ selectedProfession, onSelectProfession, hasCastingToolAccess }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { navigate } = usePageContext();
  const { isLoggedIn } = useAppContext();
  const { isDesktop, isMobile } = useDeviceTypeLayouts();
  const { setIsLoginDialog } = useDialogs();
  const showPageAccessPortalForCasting = useShowPageAccessPortalForCasting();

  return (
    <div>
      <HorizontalScroller hideArrow>
        <div className={classes.proViewContent}>
          {!isDesktop && (
            <>
              <div className={classes.castingToolContent__titleContainer}>
                <Typography size="13" secondaryFont className={classes.castingToolContent__title}>
                  {t(`${TP}.FN_CASTINGTOOL`)} &amp; {t(`${TP}.TALENT_SEARCH`)}
                </Typography>
                <PricingPlanInfo />
              </div>
              <div className={classes.divider} />
            </>
          )}
          <div className={classes.proViewContent__tabs}>
            {isDesktop &&
              CASTING_TOOL_TABS.map(tab => (
                <Fragment key={tab.key}>
                  <TextButton
                    styles={{ root: classes.proViewContent__tab }}
                    isLink
                    {...navigate.getLinkProps({
                      baseRoute: ROUTE_RESERVED_KEYWORDS.casting,
                      tab: tab.key,
                      onlyLinkProps: true,
                    })}
                    target="_blank"
                    disableUnderline
                    onClick={e => {
                      if (!isLoggedIn) {
                        e.preventDefault();
                        setIsLoginDialog({ isOpen: true });
                      }

                      if (!hasCastingToolAccess) {
                        e.preventDefault();
                        showPageAccessPortalForCasting();
                      }
                    }}
                    trackingData={{
                      ...trackingData,
                      component: COMPONENTS.CASTING_TOOL_TABS,
                      subComponent: SUB_COMPONENTS.NAVIGATE_CTA,
                    }}
                  >
                    <Typography size="12">{t(tab.title)}</Typography>
                    <SpriteIcon icon="keyboard_arrow_down" size={16} className={classes.proViewContent__arrowIcon} />
                  </TextButton>
                  {isDesktop && <div className={classes.divider} />}
                </Fragment>
              ))}
            {isMobile || isDesktop ? (
              <div className={classes.proViewContent__professionDropdown}>
                {professionFields?.map((fieldProps, index) => (
                  <Fragment key={index}>
                    <ArtistProfessionDropdown
                      className={classnames(classes.professionDropdown, {
                        [classes.professionDropdown_profession]: [
                          PROFESSION_TYPES.PROFESSION,
                          PROFESSION_TYPES.VOICE_TYPE,
                        ].includes(fieldProps.types[0]),
                        [classes.proViewContent__professionDropdown_first]: index === 0,
                      })}
                      value={getSelectedValueByProfessionType(selectedProfession, fieldProps.types)}
                      onChange={onSelectProfession}
                      variant="standard"
                      {...fieldProps}
                    />
                    {index !== professionFields.length - 1 && isDesktop && <div className={classes.proViewDivider} />}
                  </Fragment>
                ))}
              </div>
            ) : (
              <div className={classes.castingToolContent__searchContainer}>
                <div className={classes.advancedSearch}>
                  <AdvancedSearch isCasting trackingData={castingToolTrackingData} />
                </div>
                <History isCasting trackingData={castingToolTrackingData} />
              </div>
            )}
          </div>
        </div>
      </HorizontalScroller>
      {!isMobile && !isDesktop && (
        <HorizontalScroller hideArrow>
          <div className={classes.proViewContent__castingTabs}>
            {CASTING_TOOL_TABS.map(tab => (
              <Fragment key={tab.key}>
                <TextButton
                  styles={{ root: classes.proViewContent__tab }}
                  isLink
                  {...navigate.getLinkProps({
                    baseRoute: ROUTE_RESERVED_KEYWORDS.casting,
                    tab: tab.key,
                    onlyLinkProps: true,
                  })}
                  onClick={e => {
                    if (!isLoggedIn) {
                      e.preventDefault();
                      setIsLoginDialog({ isOpen: true });
                    }

                    if (!hasCastingToolAccess) {
                      e.preventDefault();
                      showPageAccessPortalForCasting();
                    }
                  }}
                  target="_blank"
                  disableUnderline
                >
                  <Typography size="12">{t(tab.title)}</Typography>
                  <SpriteIcon icon="keyboard_arrow_down" size={16} className={classes.proViewContent__arrowIcon} />
                </TextButton>
                {isDesktop && <div className={classes.divider} />}
              </Fragment>
            ))}
            <div className={classes.proViewContent__professionDropdown}>
              {professionFields?.map((fieldProps, index) => (
                <Fragment key={index}>
                  <ArtistProfessionDropdown
                    className={classnames(classes.professionDropdown, {
                      [classes.professionDropdown_profession]: [
                        PROFESSION_TYPES.PROFESSION,
                        PROFESSION_TYPES.VOICE_TYPE,
                      ].includes(fieldProps.types[0]),
                      [classes.proViewContent__professionDropdown_first]: index === 0,
                    })}
                    value={getSelectedValueByProfessionType(selectedProfession, fieldProps.types)}
                    onChange={onSelectProfession}
                    variant="standard"
                    {...fieldProps}
                  />
                  {index !== professionFields.length - 1 && isDesktop && <div className={classes.proViewDivider} />}
                </Fragment>
              ))}
            </div>
          </div>
        </HorizontalScroller>
      )}
    </div>
  );
};

const CastingSearch = () => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { navigate, baseRoute, permissions } = usePageContext();
  const { obRouteContext } = useAppContext();
  const { entity } = obRouteContext?.page || {};
  const { linkProps } = obRouteContext;
  const { filters, tab } = linkProps || {};
  const { isDesktop } = useDeviceTypeLayouts();
  const selectedProfession = useMemo(() => filters?.[FILTER_TYPES.PROFESSION]?.[0]?.entity, [filters]);
  const activeTabKey = tab;
  const isJumpInTab = activeTabKey === CASTING_RESULT_TYPES_VALUES.JUMP_IN;
  const isRoleDebutTab = activeTabKey === CASTING_RESULT_TYPES_VALUES.ROLE_DEBUT;
  const isWhoFilter = filters?.[FILTER_TYPES.WHO]?.length > 0;
  const { castingToolPermissions, isAdmin } = permissions;
  const hasCastingToolAccess = isAdmin || castingToolPermissions?.hasAccess;
  const showProView =
    entity?.id || isWhoFilter || (baseRoute === ROUTE_RESERVED_KEYWORDS.artists && hasCastingToolAccess);

  const onSelectProfession = useCallback(
    (e, profession) => {
      navigate.to(
        navigate.getLinkProps({
          entityType: ENTITY_TYPES.PROFILE,
          filters: {
            [FILTER_TYPES.PROFESSION]: [
              {
                entityType: ENTITY_TYPES.PROFESSION,
                entity: profession,
              },
            ],
          },
          onlyLinkProps: true,
        }),
      );
    },
    [navigate],
  );

  if (!isDesktop) {
    if (entity?.id) {
      return (
        <ProViewContent
          selectedProfession={selectedProfession}
          onSelectProfession={onSelectProfession}
          hasCastingToolAccess={hasCastingToolAccess}
        />
      );
    }

    return (
      <CastingSearchSearchContent
        isJumpInTab={isJumpInTab}
        isRoleDebutTab={isRoleDebutTab}
        activeTabKey={activeTabKey}
        hasCastingToolAccess={hasCastingToolAccess}
      />
    );
  }

  return (
    <div className={classes.root}>
      <div className={classnames(classes.root__searchTop, { [classes.root__searchTop_casting]: !entity?.id })}>
        <LinkButton
          {...navigate.getLinkProps({
            baseRoute: ROUTE_RESERVED_KEYWORDS.casting,
            onlyLinkProps: true,
          })}
          isLink
          variant="text"
          disableUnderline
          trackingData={{
            ...trackingData,
            component: COMPONENTS.CASTING_TOOL_CTA,
          }}
        >
          <Typography size="16" weight="medium" secondaryFont className={classes.castingToolContent__title}>
            {t(`${TP}.FN_CASTINGTOOL`)} &amp; {t(`${TP}.TALENT_SEARCH`)}
          </Typography>
        </LinkButton>
        <div className={classes.pricingPlanInfo}>
          <PricingPlanInfo />
        </div>
        <div className={classes.root__search}>
          <HeaderSearch isCasting trackingData={oldSearchTrackingData} />
        </div>
        <div className={classes.searchDivider} />
        <History isCasting trackingData={trackingData} />
      </div>
      <div
        className={classnames(classes.root__searchBottom, {
          [classes.root__searchBottomNoAccess]: !hasCastingToolAccess && !entity?.id,
        })}
      >
        {showProView ? (
          <ProViewContent
            selectedProfession={selectedProfession}
            onSelectProfession={onSelectProfession}
            hasCastingToolAccess={hasCastingToolAccess}
            trackingData={trackingData}
          />
        ) : (
          <CastingSearchSearchContent
            isJumpInTab={isJumpInTab}
            isRoleDebutTab={isRoleDebutTab}
            activeTabKey={activeTabKey}
            selectedProfession={selectedProfession}
            onSelectProfession={onSelectProfession}
            hasCastingToolAccess={hasCastingToolAccess}
          />
        )}
      </div>
    </div>
  );
};

export default CastingSearch;
